import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { FormattedMessage } from 'react-intl';

import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import { bookingStatus } from 'types/booking';

import { Row } from 'components/shared/Layout';
import CancelSpecificBookingItemsCta from './CancelSpecificBookingItemsCta';
import CloneExternalTicketsCta from './CloneExternalTicketsCta';
import ExchangeCta from './ExchangeCta';
import FlowForwardCta from './FlowForwardCta';
import MissedDepartureCta from './MissedDepartureCta';
import UpgradeCta from './UpgradeCta';
import SendConfirmationCta from './SendBookingConfirmationCta';
import PrrRefundCta from './PrrRefundCta';
import CheckoutCta from './CheckoutCta';
import CheckinCta from './CheckInCta';
import PrintCta from './PrintCta';
import CancelVoucherCta from './CancelVoucherCta';
import GenerateTicketsPerPaxCta from './GenerateTicketsPerPaxCta';
import { CtaButton } from './AfterSalesCtas.styles';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { isExchangingASubsetOfPassengers } from 'utils/summatedFares';
import { EUROSTAR_CARRIERS } from 'pages/BookingDetails/GenerateTickets/GenerateTickets.utils';

import {
  checkItemsAttribute,
  noItemsSelectedOrStatusNotAllowed,
  getRefunds,
  getVoucherRefunds,
  covidVoucherRefunded,
  selectedItemsArePartOfASummatedFare,
  disableAfterSalesForSubscription,
  selectedProductsThroughFare,
  selectedSegmentsContainsChildren,
  cancellationWillResultInChildAlone,
  exchangeWillResultInChildAlone,
  selectedProductsHaveAssociatedNonSelectedExternalCarrierProducts,
  selectedProductsFromItemRefs,
  checkIsEurostarRedAndBlue,
  areLoyaltyCompanionTicketsSelected
} from './AfterSalesCtas.utils';
import GenerateQRCodesCta from './GenerateQRCodes';
import AddPassengersCta from './AddPassengersCta';
import { useLazyQuery } from '@apollo/client';
import { GET_CUSTOMER_DETAILS, GET_PROFILE, IS_ELIGIBLE_TO_TRAVEL_PER_TRAVEL_DOCS } from '../../../../store/queries';
import RedeemCompanionVouchersCta from './RedeemCompanionVoucherCta';
import config from '../../../../utils/config';
import { TICKET_TYPES } from '../../../../types/ticket';
import { getIsPassportRequired } from '../../../../utils/ticket';
import AgentPortalError from '../../../../errors/AgentPortalError';
import { isGroupBooking } from 'utils/isGroupBooking';
import { brandCode } from 'constants/brandCodes';
import { hasDBLeg } from 'utils/segmentTypes';
import { productContainSubscriptionFare } from 'utils/subscriptionFares';
import { canUserCloneExternalTickets } from './CloneExternalTicketsCta/CloneExternalTicketsCta.utils';

const IS_ADVANCED_PASSENGER_INFORMATION_ENABLED = config('ENABLE_ADVANCED_PASSENGER_INFORMATION', { canOverride: true }) === 'true';
const AfterSalesCtas = ({
  booking,
  user,
  selectedItemRefs,
  setMissedDepartureNoTrainAvailable,
  isOutboundSummatedFare,
  isInboundSummatedFare,
  history,
  refetchBooking
}) => {
  const { outbound, inbound, status, displayStatus } = booking;
  const productsFromItemRefs = selectedProductsFromItemRefs({ segments: [...outbound, ...inbound], selectedItemRefs });
  const nonEurostarProductsFromItemRefs = selectedProductsFromItemRefs({
    segments: [
      ...outbound.filter(({ serviceType }) => !EUROSTAR_CARRIERS.includes(serviceType?.code)),
      ...inbound.filter(({ serviceType }) => !EUROSTAR_CARRIERS.includes(serviceType?.code))
    ],
    selectedItemRefs
  });
  const suspendedTicketsSelected = productsFromItemRefs.some(({ isSuspended }) => isSuspended);
  const placeholderTicketsSelected = productsFromItemRefs.some(({ isPlaceholder }) => isPlaceholder);
  const hasExpiredNonEurostarTickets = nonEurostarProductsFromItemRefs.some(({ isExpired }) => isExpired);
  const { CONFIRMED, PENDING_REFUND, OVERPAID, TEMPORARILY_ON_HOLD, VOIDED, UNDERPAID, ON_HOLD } = bookingStatus;
  const { isMigratedMulti, wasPurchasedWithSubscription, productFamilyGroup } = booking;
  const [subscription, setSubscription] = useState(null);

  const loyaltyCompanionTicketsSelected = areLoyaltyCompanionTicketsSelected({
    segments: [...(booking.outbound || []), ...(booking.inbound || [])],
    selectedItemRefs
  });
  const groupBooking = isGroupBooking(productFamilyGroup);
  const segmentContainsChildren = selectedSegmentsContainsChildren({ selectedItemRefs, booking });
  const shouldCheckForChildrenAlone = !groupBooking && segmentContainsChildren;
  const exchangeWillCreateChildrenAlone = exchangeWillResultInChildAlone({ shouldCheckForChildrenAlone, selectedItemRefs, booking });
  const cancelationWillCreateChildrenAlone = cancellationWillResultInChildAlone({ shouldCheckForChildrenAlone, selectedItemRefs, booking });
  const isExchangingSubsetOfPaxsInPALBooking =
    productFamilyGroup === productFamilyCodes.PALS &&
    (isExchangingASubsetOfPassengers(booking.outbound, selectedItemRefs) ||
      isExchangingASubsetOfPassengers(booking.inbound, selectedItemRefs));

  const [getProfile, { data: profileData }] = useLazyQuery(GET_PROFILE);
  const [getCustomerInfo, { data }] = useLazyQuery(GET_CUSTOMER_DETAILS, {
    variables: {
      membershipNumber: booking.passengers?.[0].loyaltyNumber
    },
    fetchPolicy: 'network-only'
  });

  const [isEligibleToTravel, { data: isEligibleToTravelData }] = useLazyQuery(IS_ELIGIBLE_TO_TRAVEL_PER_TRAVEL_DOCS, {
    onError: (error) => {
      throw new AgentPortalError(error);
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    try {
      if (booking.passengers[0].lastName?.length) {
        isEligibleToTravel({ variables: { reference: booking.reference, lastName: booking.passengers[0]?.lastName } });
      }
    } catch (error) {
      throw new AgentPortalError(error);
    }
  }, [booking, isEligibleToTravel]);

  let checkPassportRequirement = false;

  if (IS_ADVANCED_PASSENGER_INFORMATION_ENABLED && isEligibleToTravelData) {
    const products = booking.outbound
      .concat(booking.inbound)
      .map((l) => l.products)
      .flat();

    const isAnyRequired = products.some((prod) => prod.passenger.travelDocumentsRequired.some((tdr) => tdr.required === true));

    const isAnyNotCompleted = isEligibleToTravelData?.isEligibleToTravelPerTravelDocs.some((doc) => !doc.readyToTravel);

    if (isAnyRequired && isAnyNotCompleted) {
      checkPassportRequirement = true;
    }
  }

  const pdf_passport_required = getIsPassportRequired(user, booking, TICKET_TYPES.PDF);
  const qr_passport_required = getIsPassportRequired(user, booking, TICKET_TYPES.QR);
  const atb_passport_required = getIsPassportRequired(user, booking, TICKET_TYPES.ATB);

  useEffect(() => {
    if (!wasPurchasedWithSubscription) {
      return;
    }

    getCustomerInfo();
  }, [wasPurchasedWithSubscription, booking, getCustomerInfo]);

  const customerInfo = data && (data.getCustomerInfo || data.getCustomerDetails);

  useEffect(() => {
    if (wasPurchasedWithSubscription && customerInfo) {
      getProfile({
        variables: {
          customerId: customerInfo.profile.customerId,
          subscriptionSearchType: 'CURRENT'
        }
      });
    }
  }, [getProfile, wasPurchasedWithSubscription, customerInfo]);

  useEffect(() => {
    if (!profileData) {
      return;
    }

    const subscriptionIdentifier = booking.passengers?.[0].discountCards.find((discountCard) =>
      discountCard.code.startsWith('TH_PASS')
    )?.identifier;

    setSubscription(profileData.profile.subscriptions.find((s) => s.travelPassReference === subscriptionIdentifier));

    return () => {
      setSubscription(null);
    };
  }, [profileData, setSubscription, booking.passengers]);

  const areItemsInASummatedFare = selectedItemsArePartOfASummatedFare(
    booking,
    selectedItemRefs,
    isOutboundSummatedFare,
    isInboundSummatedFare
  );

  const areItemsInAThroughFare = selectedProductsThroughFare(selectedItemRefs, booking);

  const isAnySelectedTicketPrrRefunded = checkItemsAttribute({
    itemRefs: selectedItemRefs,
    journey: [...outbound, ...inbound],
    attribute: 'isPrrRefunded'
  });
  const isAnySelectedTicketPrrAttempted = checkItemsAttribute({
    itemRefs: selectedItemRefs,
    journey: [...outbound, ...inbound],
    attribute: 'isPrrAttempted'
  });

  const disableCTAsIfReadOnlyOrNoSelectedItems = user.isReadOnly || selectedItemRefs.length === 0;
  const isBookingOverPaidVoidedOrUnderpaid = [UNDERPAID].includes(status) || [OVERPAID, VOIDED].includes(displayStatus);

  const disableCTAsBasedOnUserOrBookingStatus =
    disableCTAsIfReadOnlyOrNoSelectedItems || status === PENDING_REFUND || status !== CONFIRMED || isBookingOverPaidVoidedOrUnderpaid;
  const disableCTAsBasedOnSuspendedOrPlaceholder = suspendedTicketsSelected || placeholderTicketsSelected;

  const isPackageBooking = booking?.hotels?.length > 0 || booking?.isPackage;

  const isMigratedMultiOrDisabled = isMigratedMulti || disableCTAsBasedOnUserOrBookingStatus;

  const isExchangedUpgradedCancelledDisabled =
    disableCTAsIfReadOnlyOrNoSelectedItems ||
    isMigratedMulti ||
    noItemsSelectedOrStatusNotAllowed({ selectedItemRefs, status }) ||
    isBookingOverPaidVoidedOrUnderpaid;

  const isPurchasedWithSubAndSubNotActive = disableAfterSalesForSubscription({
    wasPurchasedWithSubscription,
    agent: booking.agent,
    subscription
  });

  const disableExchangesForSubscription = () => {
    const selectedProductsContainSubscriptionFares = productContainSubscriptionFare({ products: productsFromItemRefs });
    return disableAfterSalesForSubscription({
      wasPurchasedWithSubscription: wasPurchasedWithSubscription && selectedProductsContainSubscriptionFares,
      agent: booking.agent,
      subscription
    });
  };

  const itemsThroughOrSummatedFares = areItemsInASummatedFare || areItemsInAThroughFare;

  const bookingNotConfirmedOrTempOnHoldOrOnHold = ![CONFIRMED, TEMPORARILY_ON_HOLD, ON_HOLD].includes(status);

  const isAddPassengersDisabled =
    itemsThroughOrSummatedFares ||
    isExchangedUpgradedCancelledDisabled ||
    bookingNotConfirmedOrTempOnHoldOrOnHold ||
    isBookingOverPaidVoidedOrUnderpaid ||
    isPurchasedWithSubAndSubNotActive;

  const isRedeemCompanionVoucherCtaDisabled =
    disableCTAsIfReadOnlyOrNoSelectedItems ||
    status !== CONFIRMED ||
    !booking.isLoyalty ||
    areItemsInAThroughFare ||
    isBookingOverPaidVoidedOrUnderpaid;

  const isEurostarRedAndBlue = checkIsEurostarRedAndBlue([...booking.outbound, ...booking.inbound]);

  const hasLyriaLeg = (journey) => journey.some(({ serviceType }) => serviceType.brandCode === brandCode.Lyria);
  const isLyriaBooking = hasLyriaLeg([...outbound, ...inbound]);

  const hasSNCFLeg = (journey) =>
    journey.some(({ serviceType }) => serviceType.brandCode === brandCode.INOUI || serviceType.brandCode === brandCode.SNCF);
  const isSNCFBooking = hasSNCFLeg([...outbound, ...inbound]);

  const isDBBooking = hasDBLeg([...outbound, ...inbound]);

  const hasNSLeg = (journey) => journey.some(({ serviceType }) => serviceType.brandCode === brandCode.NS);
  const isNSBooking = hasNSLeg([...outbound, ...inbound]);

  const hasSNCBLeg = (journey) => journey.some(({ serviceType }) => serviceType.brandCode === brandCode.SNCB);
  const isSNCBBooking = hasSNCBLeg([...outbound, ...inbound]);

  const throughOrSummatedSNCB = isSNCBBooking ? false : itemsThroughOrSummatedFares;

  const isExchangeDisabled =
    disableCTAsIfReadOnlyOrNoSelectedItems ||
    isMigratedMulti ||
    noItemsSelectedOrStatusNotAllowed({ selectedItemRefs, status, isLyriaBooking, isSNCFBooking, isDBBooking, isNSBooking }) ||
    disableExchangesForSubscription() ||
    isBookingOverPaidVoidedOrUnderpaid ||
    isAnySelectedTicketPrrAttempted;

  const shouldShowCloneExternalTicketsCta = canUserCloneExternalTickets(user);

  return (
    <Row start={3} span={12} data-cy="aftersale-ctas">
      <FlowForwardCta
        disabled={
          throughOrSummatedSNCB ||
          isMigratedMultiOrDisabled ||
          isAnySelectedTicketPrrAttempted ||
          suspendedTicketsSelected ||
          placeholderTicketsSelected
        }
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        label={<FormattedMessage id="afterSales.flowForward" />}
        willResultInChildAlone={exchangeWillCreateChildrenAlone}
        exchangingSubsetOfPaxsInPALBooking={isExchangingSubsetOfPaxsInPALBooking}
      />
      <MissedDepartureCta
        disabled={
          throughOrSummatedSNCB ||
          isMigratedMultiOrDisabled ||
          isAnySelectedTicketPrrAttempted ||
          suspendedTicketsSelected ||
          placeholderTicketsSelected
        }
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        setNoTrainAvailable={setMissedDepartureNoTrainAvailable}
        label={<FormattedMessage id="afterSales.missedDeparture" />}
        willResultInChildAlone={exchangeWillCreateChildrenAlone}
        exchangingSubsetOfPaxsInPALBooking={isExchangingSubsetOfPaxsInPALBooking}
      />
      <ExchangeCta
        disabled={isExchangeDisabled || disableCTAsBasedOnSuspendedOrPlaceholder}
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        label={<FormattedMessage id="afterSales.exchange" />}
        isPackageBooking={isPackageBooking}
        subscription={subscription}
        willResultInChildAlone={exchangeWillCreateChildrenAlone}
        exchangingSubsetOfPaxsInPALBooking={isExchangingSubsetOfPaxsInPALBooking}
      />
      <UpgradeCta
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        afterSalesShortcuts={user.afterSalesShortcuts}
        isExchangingSubsetOfPaxsInPALBooking={isExchangingSubsetOfPaxsInPALBooking}
        exchangeWillCreateChildrenAlone={exchangeWillCreateChildrenAlone}
        disabled={disableCTAsBasedOnSuspendedOrPlaceholder}
        isMigratedMultiOrDisabled={isMigratedMultiOrDisabled}
        isAnySelectedTicketPrrAttempted={isAnySelectedTicketPrrAttempted}
        itemsThroughOrSummatedFares={itemsThroughOrSummatedFares}
        areItemsInASummatedFare={areItemsInASummatedFare}
        areItemsInAThroughFare={areItemsInAThroughFare}
        isPurchasedWithSubAndSubNotActive={isPurchasedWithSubAndSubNotActive}
        isEurostarRedAndBlue={isEurostarRedAndBlue}
        isExchangedUpgradedCancelledDisabled={isExchangedUpgradedCancelledDisabled}
        isAnySelectedTicketPrrRefunded={isAnySelectedTicketPrrRefunded}
      />
      <AddPassengersCta
        disabled={isAddPassengersDisabled || isAnySelectedTicketPrrAttempted || disableCTAsBasedOnSuspendedOrPlaceholder}
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        label={<FormattedMessage id="afterSales.addPassengers" />}
      />
      <RedeemCompanionVouchersCta
        disabled={isRedeemCompanionVoucherCtaDisabled || isAnySelectedTicketPrrAttempted}
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        label={<FormattedMessage id="afterSales.redeemCompanionVoucher" />}
      />
      <CancelSpecificBookingItemsCta
        disabled={isExchangedUpgradedCancelledDisabled || isAnySelectedTicketPrrAttempted || disableCTAsBasedOnSuspendedOrPlaceholder}
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        willResultInChildAlone={cancelationWillCreateChildrenAlone}
        willHaveOrphansExternalTickets={
          areItemsInASummatedFare && selectedProductsHaveAssociatedNonSelectedExternalCarrierProducts({ booking, selectedItemRefs })
        }
      >
        <FormattedMessage id="afterSales.cancel" />
      </CancelSpecificBookingItemsCta>
      {shouldShowCloneExternalTicketsCta && (
        <CloneExternalTicketsCta
          booking={booking}
          selectedItemRefs={selectedItemRefs}
          history={history}
          disabled={isExchangedUpgradedCancelledDisabled || isAnySelectedTicketPrrAttempted || disableCTAsBasedOnSuspendedOrPlaceholder}
          refetchBooking={refetchBooking}
        />
      )}
      <PrrRefundCta
        disabled={
          loyaltyCompanionTicketsSelected ||
          isMigratedMultiOrDisabled ||
          isAnySelectedTicketPrrRefunded ||
          disableCTAsBasedOnSuspendedOrPlaceholder
        }
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        isSummatedFare={areItemsInASummatedFare}
        isThroughFare={areItemsInAThroughFare}
      />
      <CheckinCta
        disabled={disableCTAsBasedOnUserOrBookingStatus || isAnySelectedTicketPrrAttempted || disableCTAsBasedOnSuspendedOrPlaceholder}
        isOutboundSummatedFare={isOutboundSummatedFare}
        isInboundSummatedFare={isInboundSummatedFare}
        selectedItemRefs={selectedItemRefs}
        booking={booking}
      />
      <CheckoutCta
        disabled={disableCTAsBasedOnUserOrBookingStatus || isAnySelectedTicketPrrAttempted || disableCTAsBasedOnSuspendedOrPlaceholder}
        isOutboundSummatedFare={isOutboundSummatedFare}
        isInboundSummatedFare={isInboundSummatedFare}
        selectedItemRefs={selectedItemRefs}
        booking={booking}
      />
      <GenerateTicketsPerPaxCta
        disabled={disableCTAsBasedOnUserOrBookingStatus || disableCTAsBasedOnSuspendedOrPlaceholder}
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        label={<FormattedMessage id="aftersalesCTA.pdfTickets" />}
        history={history}
        isPassportRequired={checkPassportRequirement && pdf_passport_required}
        isThroughFare={areItemsInAThroughFare}
        hasExpiredNonEurostarTickets={hasExpiredNonEurostarTickets}
      />
      <PrintCta
        key="print"
        disabled={disableCTAsBasedOnUserOrBookingStatus || disableCTAsBasedOnSuspendedOrPlaceholder}
        booking={booking}
        selectedItemRefs={selectedItemRefs}
        label={<FormattedMessage id="afterSales.print" />}
        history={history}
        isPassportRequired={checkPassportRequirement && atb_passport_required}
        isThroughFare={areItemsInAThroughFare}
      />
      <GenerateQRCodesCta
        disabled={isExchangedUpgradedCancelledDisabled || areItemsInAThroughFare || disableCTAsBasedOnSuspendedOrPlaceholder}
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        label={<FormattedMessage id="afterSales.qrCodes" />}
        history={history}
        isPassportRequired={checkPassportRequirement && qr_passport_required}
      />
      <SendConfirmationCta
        disabled={isBookingOverPaidVoidedOrUnderpaid}
        label={<FormattedMessage id="afterSales.sendBookingConfirmation" />}
        bookingReference={booking.reference}
        customerEmail={booking.customer?.email}
      />
      <CancelVoucherCta
        disabled={
          !getVoucherRefunds(getRefunds(booking?.payments)) || covidVoucherRefunded(booking?.notes) || isBookingOverPaidVoidedOrUnderpaid
        }
        booking={booking}
      />
    </Row>
  );
};

AfterSalesCtas.propTypes = {
  booking: bookingShape.isRequired,
  user: userShape.isRequired,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string).isRequired,
  setMissedDepartureNoTrainAvailable: PropTypes.func.isRequired,
  isOutboundSummatedFare: PropTypes.bool,
  isInboundSummatedFare: PropTypes.bool,
  isPackageBooking: PropTypes.bool,
  history: historyShape,
  refetchBooking: PropTypes.func
};

export default AfterSalesCtas;

export { CtaButton };
